import React, { useEffect, useMemo, useState } from "react";
import stilePulsanti from "../../../interfaccia/stilePulsanti";
import { TextSkeleton } from "../../../interfaccia/loading";
import KitModaModal from "./KitModaModal";
import RicercaArticoloKit from "./RicercaArticoloKit";
import {
  componentiAttuali,
  etichettaTaglia,
  isAnnullata,
  messaggioErrore,
  statoDaConflitto,
} from "./kitModaApi";

const QUANTITA_MASSIMA = 999;

const quantitaValida = (value) => {
  const numero = Number(value);
  return Number.isInteger(numero) && numero >= 1 && numero <= QUANTITA_MASSIMA;
};

const normalizza = (value) => String(value ?? "").toUpperCase().replace(/[\s.]+/g, "");

/** Taglia proposta: l'unica disponibile, oppure l'unica con la stessa descrizione del padre. */
const tagliaSuggerita = (taglie, padre) => {
  if (taglie.length === 1) return taglie[0].tripletta;
  const uguali = taglie.filter(
    (taglia) => normalizza(taglia.tagliaDescrizione) && normalizza(taglia.tagliaDescrizione) === normalizza(padre.tagliaDescrizione),
  );
  return uguali.length === 1 ? uguali[0].tripletta : null;
};

const righeDaCache = (cache) =>
  cache?.esito === "KIT"
    ? cache.componenti.map((componente) => ({
        codVS: componente.reference,
        nome: componente.modaNome || componente.name,
        tripletta: componente.itemReference,
        tagliaLabel: componente.itemAttributes,
        quantita: componente.itemsInKit,
      }))
    : [];

const righeDaProposta = (riga, stato) =>
  (riga.proposta ?? []).map((proposta) => {
    const schema = stato.schema.find((item) => item.componenteCodVS === proposta.componenteCodVS);
    const taglia = (stato.taglieComponenti[proposta.componenteCodVS] ?? []).find(
      (item) => item.tripletta === proposta.tripletta,
    );
    return {
      codVS: proposta.componenteCodVS,
      nome: schema?.nome ?? null,
      tripletta: proposta.tripletta,
      tagliaLabel: taglia ? etichettaTaglia(taglia) : null,
      quantita: proposta.quantita,
    };
  });

/** Differenze fra il kit attuale e quello che si sta per inviare, per il riepilogo. */
export const differenzeKit = (prima, dopo) => {
  const attuali = new Map(prima.map((riga) => [riga.tripletta, riga]));
  const nuovi = new Map(dopo.map((riga) => [riga.tripletta, riga]));
  const righe = [];
  for (const riga of dopo) {
    const vecchia = attuali.get(riga.tripletta);
    if (!vecchia) righe.push({ ...riga, tipo: "aggiunto" });
    else if (Number(vecchia.quantita) !== Number(riga.quantita))
      righe.push({ ...riga, tipo: "quantita", quantitaPrima: vecchia.quantita });
    else righe.push({ ...riga, tipo: "invariato" });
  }
  for (const riga of prima) {
    if (!nuovi.has(riga.tripletta)) righe.push({ ...riga, tipo: "tolto" });
  }
  return righe;
};

const ETICHETTE_DIFFERENZA = {
  aggiunto: "Aggiunto",
  tolto: "Tolto",
  quantita: "Quantità cambiata",
  invariato: "Invariato",
};

function SceltaTaglia({ api, articolo, padre, taglieNote, selezionata, onScegli }) {
  const [taglie, setTaglie] = useState(taglieNote);
  const [error, setError] = useState("");
  const [scelta, setScelta] = useState(selezionata);

  useEffect(() => {
    if (taglieNote) return undefined;
    const controller = new AbortController();
    api
      .taglie(articolo.codVS, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setTaglie(data);
        setScelta((attuale) => attuale ?? tagliaSuggerita(data, padre));
      })
      .catch((err) => {
        if (controller.signal.aborted || isAnnullata(err)) return;
        setError(messaggioErrore(err, "Taglie dell'articolo non disponibili"));
      });
    return () => controller.abort();
  }, [api, articolo.codVS, padre, taglieNote]);

  useEffect(() => {
    if (taglieNote) setScelta((attuale) => attuale ?? tagliaSuggerita(taglieNote, padre));
  }, [padre, taglieNote]);

  if (error) {
    return (
      <p className="mt0 dark-red" role="alert">
        {error}
      </p>
    );
  }
  if (!taglie) return <TextSkeleton lines={3} lineHeight={16} />;
  if (!taglie.length) {
    return <p className="mt0">L'articolo {articolo.codVS} non ha taglie con barcode attivo in Moda.</p>;
  }
  return (
    <fieldset className="bn pa0 ma0">
      <legend className="b mb2">
        Taglia di {articolo.codVS} {articolo.nome ? `· ${articolo.nome}` : ""}
      </legend>
      {taglie.map((taglia) => (
        <label key={taglia.tripletta} className="db pv1 pointer">
          <input
            type="radio"
            name="kitModaTagliaComponente"
            className="mr2"
            checked={scelta === taglia.tripletta}
            onChange={() => setScelta(taglia.tripletta)}
          />
          {etichettaTaglia(taglia)}
          <span className="f7 scrittaColMedio">
            {" "}
            · {taglia.tripletta}
            {taglia.barcodes?.length ? ` · ${taglia.barcodes.join(", ")}` : ""}
          </span>
        </label>
      ))}
      <button
        type="button"
        className={`${stilePulsanti.buttonClassesGreen} mt2`}
        disabled={!scelta}
        onClick={() => onScegli(taglie.find((taglia) => taglia.tripletta === scelta))}
      >
        Scegli questa taglia
      </button>
    </fieldset>
  );
}

/**
 * Wizard per il kit di una taglia: elenco dei componenti, "Aggiungi componente"
 * (articolo → taglia → quantita'), riepilogo con le differenze e invio a Prestashop.
 */
function WizardKitTaglia({ codVS, riga, stato, api, onClose, onSent, onConflict }) {
  // Dopo un 409 la riga arriva gia' aggiornata dal genitore: il riepilogo confronta
  // con il kit ora su Prestashop e l'invio successivo e' forzato ("Invia comunque").
  const [conflitto, setConflitto] = useState("");
  const iniziali = useMemo(() => {
    const daCache = righeDaCache(riga.cache);
    return daCache.length ? daCache : righeDaProposta(riga, stato);
  }, [riga, stato]);
  const attuali = useMemo(() => righeDaCache(riga.cache), [riga.cache]);
  const [righe, setRighe] = useState(iniziali);
  const [passo, setPasso] = useState("elenco");
  const [articolo, setArticolo] = useState(null);
  const [indiceTaglia, setIndiceTaglia] = useState(null);
  const [nuovaTaglia, setNuovaTaglia] = useState(null);
  const [quantita, setQuantita] = useState("1");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const padre = useMemo(
    () => ({ tagliaDescrizione: riga.tagliaDescrizione, varianteDescrizione: riga.varianteDescrizione }),
    [riga.tagliaDescrizione, riga.varianteDescrizione],
  );
  const completa = righe.every((item) => item.tripletta && quantitaValida(item.quantita));
  const triplette = righe.map((item) => item.tripletta).filter(Boolean);
  const duplicati = triplette.length !== new Set(triplette).size;
  const svuota = righe.length === 0;
  const differenze = differenzeKit(attuali, righe);
  const cambia = differenze.some((item) => item.tipo !== "invariato");
  const etichettaPadre = etichettaTaglia(riga);
  const quantitaSchema = (codice) =>
    stato.schema.find((item) => item.componenteCodVS === codice)?.quantita ?? 1;

  const invia = async () => {
    setSending(true);
    setError("");
    try {
      const data = await api.salvaTaglia(
        codVS,
        riga.tripletta,
        righe.map((item) => ({ tripletta: item.tripletta, quantita: Number(item.quantita) })),
        componentiAttuali(riga.cache),
        Boolean(conflitto),
      );
      onSent(data);
    } catch (err) {
      const aggiornato = statoDaConflitto(err);
      if (aggiornato) {
        onConflict(aggiornato);
        setConflitto(
          "Nel frattempo il kit su Prestashop è cambiato: il riepilogo ora lo confronta con il kit attuale. Puoi inviare comunque o tornare indietro.",
        );
        setPasso("riepilogo");
        setSending(false);
        return;
      }
      setError(messaggioErrore(err, "Invio del kit non riuscito"));
      setSending(false);
    }
  };

  const vaiA = (nuovoPasso) => {
    setError("");
    setPasso(nuovoPasso);
  };

  let footer = null;
  if (passo === "elenco") {
    footer = (
      <div className="flex flex-wrap justify-between items-center">
        <div>
          <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={() => vaiA("articolo")}>
            Aggiungi componente
          </button>
          {riga.cache?.esito === "KIT" && riga.proposta?.length > 0 && (
            <button
              type="button"
              className={stilePulsanti.buttonClassesWhite}
              onClick={() => setRighe(righeDaProposta(riga, stato))}
            >
              Riparti dalla composizione
            </button>
          )}
        </div>
        <div>
          <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className={stilePulsanti.buttonClassesGreen}
            onClick={() => vaiA("riepilogo")}
            disabled={!completa || duplicati || !cambia || (svuota && attuali.length === 0)}
          >
            Riepilogo
          </button>
        </div>
      </div>
    );
  } else if (passo === "riepilogo") {
    footer = (
      <div className="flex justify-between">
        <button type="button" className={stilePulsanti.buttonClassesWhite} onClick={() => vaiA("elenco")} disabled={sending}>
          Indietro
        </button>
        <button
          type="button"
          className={svuota ? stilePulsanti.buttonClassesRed : stilePulsanti.buttonClassesGreen}
          onClick={invia}
          disabled={sending}
        >
          {sending
            ? "Invio..."
            : conflitto
              ? "Invia comunque"
              : svuota
                ? "Rimuovi il kit da Prestashop"
                : "Invia a Prestashop"}
        </button>
      </div>
    );
  } else {
    footer = (
      <button
        type="button"
        className={stilePulsanti.buttonClassesWhite}
        onClick={() => {
          setArticolo(null);
          setIndiceTaglia(null);
          vaiA("elenco");
        }}
      >
        Torna all'elenco
      </button>
    );
  }

  return (
    <KitModaModal
      title={`Kit della taglia ${etichettaPadre}`}
      subtitle={`${codVS} · ${riga.tripletta}`}
      onClose={onClose}
      busy={sending}
      footer={footer}
    >
      {error && (
        <p className="mt0 dark-red" role="alert">
          {error}
        </p>
      )}
      {conflitto && (
        <p className="mt0 dark-red" role="alert">
          {conflitto}
        </p>
      )}

      {passo === "elenco" && (
        <>
          {righe.length === 0 ? (
            <p className="mt0">
              {attuali.length
                ? "Hai tolto tutti i componenti: dal riepilogo puoi rimuovere il kit da Prestashop."
                : "Nessun componente. Aggiungi il primo articolo che costituisce la giacenza del kit."}
            </p>
          ) : (
            <table className="collapse w-100" aria-label="Componenti del kit">
              <thead>
                <tr className="tl bb bordoColAlt">
                  <th className="pv2 pr2">Articolo</th>
                  <th className="pv2 pr2">Taglia</th>
                  <th className="pv2 pr2">Quantità</th>
                  <th className="pv2" aria-label="Azioni" />
                </tr>
              </thead>
              <tbody>
                {righe.map((item, index) => (
                  <tr key={`${item.codVS}-${index}`} className="bb bordoColAlt v-top">
                    <td className="pv2 pr2">
                      <span className="b">{item.codVS}</span> {item.nome || ""}
                    </td>
                    <td className="pv2 pr2">
                      {item.tripletta ? (
                        <>
                          {item.tagliaLabel || item.tripletta}
                          <span className="db f7 scrittaColMedio">{item.tripletta}</span>
                        </>
                      ) : (
                        <span className="dark-red">Da scegliere</span>
                      )}
                      <button
                        type="button"
                        className="db mt1 f7 bn bg-transparent pa0 underline pointer scrittaColAltScuro"
                        onClick={() => {
                          setArticolo({ codVS: item.codVS, nome: item.nome });
                          setIndiceTaglia(index);
                          vaiA("taglia");
                        }}
                        aria-label={`Scegli la taglia di ${item.codVS}`}
                      >
                        {item.tripletta ? "Cambia taglia" : "Scegli taglia"}
                      </button>
                    </td>
                    <td className="pv2 pr2">
                      <input
                        type="number"
                        min="1"
                        max={QUANTITA_MASSIMA}
                        step="1"
                        className="ba bordoColScrittaDef-10 pa1 br2 w4"
                        value={item.quantita}
                        aria-label={`Quantità di ${item.codVS}`}
                        onChange={(event) =>
                          setRighe((attualiRighe) =>
                            attualiRighe.map((r, i) => (i === index ? { ...r, quantita: event.target.value } : r)),
                          )
                        }
                      />
                    </td>
                    <td className="pv2 tr">
                      <button
                        type="button"
                        className={stilePulsanti.buttonClassesWhite}
                        onClick={() => setRighe((attualiRighe) => attualiRighe.filter((_, i) => i !== index))}
                        aria-label={`Togli ${item.codVS}`}
                      >
                        Togli
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {duplicati && (
            <p className="mt2 dark-red" role="alert">
              La stessa taglia compare due volte: tienila una volta con la quantità totale.
            </p>
          )}
          {!cambia && righe.length > 0 && <p className="mt2 f6 scrittaColMedio">Nessuna modifica rispetto al kit attuale.</p>}
        </>
      )}

      {passo === "articolo" && (
        <RicercaArticoloKit
          api={api}
          escludi={[codVS]}
          onSelect={(scelto) => {
            setArticolo(scelto);
            setIndiceTaglia(null);
            vaiA("taglia");
          }}
        />
      )}

      {passo === "taglia" && articolo && (
        <SceltaTaglia
          api={api}
          articolo={articolo}
          padre={padre}
          taglieNote={stato.taglieComponenti?.[articolo.codVS] ?? null}
          selezionata={indiceTaglia !== null ? righe[indiceTaglia]?.tripletta ?? null : null}
          onScegli={(taglia) => {
            if (indiceTaglia !== null) {
              setRighe((attualiRighe) =>
                attualiRighe.map((r, i) =>
                  i === indiceTaglia ? { ...r, tripletta: taglia.tripletta, tagliaLabel: etichettaTaglia(taglia) } : r,
                ),
              );
              setIndiceTaglia(null);
              vaiA("elenco");
              return;
            }
            setNuovaTaglia(taglia);
            setQuantita(String(quantitaSchema(articolo.codVS)));
            vaiA("quantita");
          }}
        />
      )}

      {passo === "quantita" && articolo && nuovaTaglia && (
        <div>
          <p className="mt0">
            <span className="b">{articolo.codVS}</span> {articolo.nome || ""} · {etichettaTaglia(nuovaTaglia)}
          </p>
          <label className="db mb2 b" htmlFor="kitModaQuantitaComponente">
            Quanti pezzi servono per un kit?
          </label>
          <input
            id="kitModaQuantitaComponente"
            type="number"
            min="1"
            max={QUANTITA_MASSIMA}
            step="1"
            className="ba bordoColScrittaDef-10 pa2 br2 w4"
            value={quantita}
            onChange={(event) => setQuantita(event.target.value)}
            autoFocus
          />
          <div className="mt3">
            <button
              type="button"
              className={stilePulsanti.buttonClassesGreen}
              disabled={!quantitaValida(quantita)}
              onClick={() => {
                setRighe((attualiRighe) => [
                  ...attualiRighe,
                  {
                    codVS: articolo.codVS,
                    nome: articolo.nome,
                    tripletta: nuovaTaglia.tripletta,
                    tagliaLabel: etichettaTaglia(nuovaTaglia),
                    quantita: Number(quantita),
                  },
                ]);
                setArticolo(null);
                setNuovaTaglia(null);
                vaiA("elenco");
              }}
            >
              Aggiungi al kit
            </button>
          </div>
        </div>
      )}

      {passo === "riepilogo" && (
        <div>
          <p className="mt0">
            {svuota
              ? `La taglia ${etichettaPadre} smetterà di essere un kit su Prestashop.`
              : `Su Prestashop il kit della taglia ${etichettaPadre} sarà sostituito con questi componenti.`}
          </p>
          <table className="collapse w-100" aria-label="Riepilogo modifiche">
            <tbody>
              {differenze.map((item) => (
                <tr key={`${item.tipo}-${item.tripletta}`} className="bb bordoColAlt">
                  <td className={`pv2 pr2 b ${item.tipo === "tolto" ? "dark-red" : ""}`}>{ETICHETTE_DIFFERENZA[item.tipo]}</td>
                  <td className="pv2 pr2">
                    {item.tipo === "quantita" ? `${item.quantitaPrima} → ${item.quantita}` : item.quantita} ×{" "}
                    <span className="b">{item.codVS}</span> {item.nome || ""}
                    <span className="db f7 scrittaColMedio">{item.tagliaLabel || item.tripletta}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </KitModaModal>
  );
}

export default WizardKitTaglia;
